import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { Box, IconButton, Stack } from "@mui/material";
import { MRT_ColumnDef, MRT_PaginationState, MRT_SortingState, MRT_ColumnFiltersState } from "material-react-table";
import { PrimaryGrid } from "src/components/Grid/layout/PrimaryGrid";
import { GridLink } from "src/components/Grid/GridLink";
import { GridText } from "src/components/Grid/GridText";
import { formatDateTime } from "src/utils/formatting";
import i18next from "i18next";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopy from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import TuneIcon from "@mui/icons-material/Tune";
import { FoodsIcon, FoodsTags } from "@foodpilot/foods";
import { TextField } from "@mui/material";
import { useGridColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useNavigate } from "react-router";
import { Dispatch, SetStateAction } from "react";
import { PuckExtension } from "src/api/client-api/puckExtensions/useExtensions";
import { CompanySector } from "src/api/client-api/companySectors/useSupplierSectors";
import { URLHandler } from "src/services/URLHandler";
import { useRoles } from "src/api/client-api/roles/useRoles";
import { CREATE, DELETE, MODIFY, PUCK_EXTENSION, useFoodpilotContext } from "src/context/FoodpilotContext";

const upper = (v: string) => v.toUpperCase();

type DynaBlockTableProps = {
    extensions: PuckExtension[];
    allSectors: CompanySector[];
    onDelete: (extensionId: number) => void;
    onUpdateName: (extensionId: number, name: string) => void;
    onUpdateStatus?: (extensionId: number, status: string) => void;
    onOpenSettings?: (extension: PuckExtension) => void;
    pagination: MRT_PaginationState;
    setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
    sorting: MRT_SortingState;
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>;
    columnFilters: MRT_ColumnFiltersState;
    setColumnFilters: Dispatch<SetStateAction<MRT_ColumnFiltersState>>;
    totalItems: number;
    isServerSide: boolean;
};

export const DynaBlockTable = (props: DynaBlockTableProps) => {
    const {
        extensions,
        allSectors,
        onDelete,
        onUpdateName,
        onUpdateStatus,
        onOpenSettings,
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        totalItems,
        isServerSide,
    } = props;

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getRoleDisplayName } = useRoles();
    const { canUser } = useFoodpilotContext();
    const canCreate = canUser(CREATE, PUCK_EXTENSION);
    const canModify = canUser(MODIFY, PUCK_EXTENSION);
    const canDelete = canUser(DELETE, PUCK_EXTENSION);

    const [editingName, setEditingName] = useState<number | null>(null);
    const [tempName, setTempName] = useState("");

    const dynaBlockTableKey = "dynablocks-table";

    const handleEditName = (extension: PuckExtension) => {
        setEditingName(extension.id);
        setTempName(extension.name);
    };

    const handleSaveName = (extensionId: number) => {
        onUpdateName(extensionId, tempName);
        setEditingName(null);
        setTempName("");
    };

    const handleCancelEdit = () => {
        setEditingName(null);
        setTempName("");
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case "active":
                return t("settings.cms.dynablocks.status.active", "Active");
            case "inactive":
                return t("settings.cms.dynablocks.status.inactive", "Inactive");
            case "draft":
                return t("settings.cms.dynablocks.status.draft", "Draft");
            default:
                return status;
        }
    };

    const columns = useMemo<MRT_ColumnDef<PuckExtension>[]>(
        () => [
            {
                accessorKey: "name",

                header: t("settings.cms.dynablocks.table.name", "DynaBlock name"),
                size: 300,
                Cell: ({ row }) => {
                    const extension = row.original;

                    if (editingName === extension.id) {
                        return (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <TextField
                                    value={tempName}
                                    onChange={(e) => setTempName(e.target.value)}
                                    onKeyPress={(e: React.KeyboardEvent) => {
                                        if (e.key === "Enter") {
                                            handleSaveName(extension.id);
                                        }
                                        if (e.key === "Escape") {
                                            handleCancelEdit();
                                        }
                                    }}
                                    size="small"
                                />
                                <IconButton size="small" onClick={() => handleSaveName(extension.id)} color="primary">
                                    <CheckIcon />
                                </IconButton>
                                <IconButton size="small" onClick={handleCancelEdit} color="secondary">
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        );
                    }

                    if (!canModify) {
                        return <GridText value={extension.name} />;
                    }

                    const url = URLHandler.settings.cms.editDynablock(extension.id);
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <GridLink label={extension.name} to={url} />
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditName(extension);
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    );
                },
                enableSorting: true,
                enableColumnFilter: true,
            },
            {
                accessorKey: "extensionPoint",
                header: t("settings.cms.dynablocks.table.extension_point", "Extension Point"),
                size: 250,
                Cell: ({ cell }) => {
                    const value = cell.getValue<string>();
                    return <Box sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}>{value}</Box>;
                },
                enableSorting: false,
                enableColumnFilter: true,
            },
            {
                accessorKey: "status",
                header: t("settings.cms.dynablocks.table.status", "Status"),
                size: 120,
                Cell: ({ cell, row }) => {
                    const extension = row.original;
                    const status = cell.getValue<string>();
                    const nextStatus =
                        status === "draft" ? "active"
                        : status === "active" ? "inactive"
                        : "draft";
                    const variant =
                        status === "active" ? "tags-green"
                        : status === "draft" ? "tags-grey"
                        : "tags-red";

                    if (!canModify) {
                        return <FoodsTags variant={variant} label={getStatusLabel(status)} />;
                    }

                    return (
                        <Box
                            role="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onUpdateStatus?.(extension.id, nextStatus);
                            }}
                        >
                            <FoodsTags variant={variant} label={getStatusLabel(status)} />
                        </Box>
                    );
                },
                filterVariant: "select",
                filterSelectOptions: [
                    { value: "active", label: getStatusLabel("active") },
                    { value: "inactive", label: getStatusLabel("inactive") },
                    { value: "draft", label: getStatusLabel("draft") },
                ],
                enableSorting: false,
            },
            {
                id: "allowedRoles",
                header: t("settings.cms.pages.table.roles", "Roles"),
                size: 250,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }) => {
                    const roles = row.original.allowedRoles ?? [];
                    return roles.length > 0 ?
                            <Stack direction="row" gap={1} sx={{ flexWrap: "wrap", py: 1 }}>
                                {roles.map((role) => (
                                    <Box
                                        key={role}
                                        {...(canModify ?
                                            {
                                                role: "button",
                                                onClick: (e: React.MouseEvent) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onOpenSettings?.(row.original);
                                                },
                                            }
                                        :   {})}
                                    >
                                        <FoodsTags variant="tags-black" label={getRoleDisplayName(role)} />
                                    </Box>
                                ))}
                            </Stack>
                        :   <Box
                                {...(canModify ?
                                    {
                                        role: "button",
                                        onClick: (e: React.MouseEvent) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onOpenSettings?.(row.original);
                                        },
                                    }
                                :   {})}
                            >
                                <FoodsTags
                                    variant="tags-green"
                                    label={upper(t("settings.cms.pages.roles.public", "Publicly accessible"))}
                                />
                            </Box>;
                },
            },
            {
                id: "sectors",
                header: t("settings.cms.pages.table.sector", "Sector"),
                size: 250,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }) => {
                    const sectorIris = row.original.sectors ?? [];
                    const names = sectorIris
                        .map((iri) => {
                            const match = iri.match(/\/(\d+)$/);
                            const id = match ? Number(match[1]) : null;
                            if (!id) return null;
                            return allSectors.find((sec) => sec.id === id)?.name ?? null;
                        })
                        .filter(Boolean) as string[];

                    return (
                        <Box
                            {...(canModify ?
                                {
                                    role: "button",
                                    onClick: (e: React.MouseEvent) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onOpenSettings?.(row.original);
                                    },
                                }
                            :   {})}
                        >
                            {names.length === 0 ?
                                <FoodsTags variant="tags-black" label={upper(t("common.none", "None"))} />
                            :   <Stack direction="row" gap={1} sx={{ flexWrap: "wrap", py: 1 }}>
                                    {names.map((name) => (
                                        <FoodsTags key={name} variant="tags-black" label={upper(name)} />
                                    ))}
                                </Stack>
                            }
                        </Box>
                    );
                },
            },
            {
                accessorKey: "priority",
                header: t("settings.cms.dynablocks.table.priority", "Priority"),
                size: 100,
                Cell: ({ cell }) => {
                    const priority = cell.getValue<number>();
                    return <Box sx={{ textAlign: "center", fontWeight: "bold" }}>{priority}</Box>;
                },
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                accessorKey: "createdAt",
                header: t("settings.cms.dynablocks.table.created_at", "Created at"),
                size: 180,
                Cell: ({ cell }) => {
                    const date = cell.getValue<string>();
                    return <GridText value={formatDateTime(new Date(date), i18next.language)} />;
                },
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                accessorKey: "updatedAt",
                header: t("settings.cms.dynablocks.table.updated_at", "Updated at"),
                size: 180,
                Cell: ({ cell }) => {
                    const date = cell.getValue<string>();
                    return <GridText value={formatDateTime(new Date(date), i18next.language)} />;
                },
                enableSorting: true,
                enableColumnFilter: false,
            },
        ],
        [t, editingName, tempName, allSectors, onOpenSettings, canModify],
    );

    const menuItems = [
        {
            label: t("settings.cms.dynablocks.actions.edit", "Edit"),
            icon: <FoodsIcon icon="edit" size={2} />,
            onClick: (extension: PuckExtension) => {
                const url = URLHandler.settings.cms.editDynablock(extension.id);
                navigate(url);
            },
            shouldShow: () => canModify,
        },
        {
            label: t("common.actions.settings", "Settings"),
            icon: <TuneIcon fontSize="small" />,
            onClick: (extension: PuckExtension) => onOpenSettings?.(extension),
            shouldShow: () => canModify,
        },
        {
            label: t("settings.cms.dynablocks.actions.duplicate", "Duplicate"),
            icon: <ContentCopy />,
            onClick: (extension: PuckExtension) => {
                // TODO: Implement duplicate functionality
                console.info("Duplicate extension:", extension.id);
            },
            shouldShow: () => canCreate,
        },
        {
            label: t("settings.cms.dynablocks.actions.delete", "Delete"),
            icon: <FoodsIcon icon="delete" size={2} />,
            onClick: (extension: PuckExtension) => onDelete(extension.id),
            shouldShow: () => canDelete,
            destructive: true,
        },
    ];

    const mrt_columns = useGridColumns(columns, dynaBlockTableKey);

    return (
        <Box>
            <PrimaryGrid
                enableDynamicRowHeight
                enableViews
                data={extensions}
                columns={mrt_columns.mrt_columns}
                gridKey={dynaBlockTableKey}
                menuItems={menuItems}
                enableRowActions={canCreate || canModify || canDelete}
                emptyMessage={t("settings.cms.dynablocks.no_dynablocks", "No DynaBlocks found")}
                filters={{
                    columnFilters: columnFilters,
                    setColumnFilters: setColumnFilters,
                }}
                sort={{
                    sorting: sorting,
                    setSorting: setSorting,
                }}
                pagination={{
                    pagination: pagination,
                    setPagination: setPagination,
                    totalItems: totalItems,
                }}
                enableServerSidePagination={isServerSide}
                enableServerSideSorting={isServerSide}
                enableServerSideFiltering={isServerSide}
                columnConfig={mrt_columns}
                onRowClick={
                    canModify ?
                        (extension: PuckExtension) => {
                            const url = URLHandler.settings.cms.editDynablock(extension.id);
                            navigate(url);
                        }
                    :   undefined
                }
            />
        </Box>
    );
};
